import { Box, Flex, Text, HStack, VStack } from '@chakra-ui/layout';
import { Button } from '@chakra-ui/react';
import { getUserFullName } from '@duke-hq/lib';
import { DeletedBadge } from '@duke-hq/ui/components/DeletedBadge';
import { UserAvatar } from '@duke-hq/ui/components/UserAvatar/UserAvatar';
import { Link as RouterLink } from 'react-router-dom';
import { CopyToClipboard } from '@duke-hq/ui/components/CopyToClipboard/CopyToClipboard';
import { CopyToClipboardPopover } from '@duke-hq/ui/components/CopyToClipboardPopover/CopyToClipboardPopover';

export interface StudentDetailsLabels {
  learner: string;
  student: string;
  viewProfile: string;
  copyEmail: string;
  deleted: string;
}

const defaultLabels: StudentDetailsLabels = {
  learner: 'Learner',
  student: 'Student',
  viewProfile: 'View profile',
  copyEmail: 'Copy email address',
  deleted: 'Deleted',
};

function StudentDetails({
  user,
  learner,
  labels,
}: {
  user: {
    id: string;
    first_name: string;
    last_name: string | null;
    picture_url: string | null;
    email: string | null;
    secondaryEmail: string | null;
    deleted_at?: Date | null;
  };
  learner?: {
    id: string;
    first_name: string;
    last_name: string | null;
    email: string | null;
    phone_number: string | null;
  } | null;
  labels?: Partial<StudentDetailsLabels>;
}): React.ReactElement {
  const l = { ...defaultLabels, ...labels };
  const profilePath = learner ? `/team/members/${learner.id}` : `/student/${user.id}/details`;

  return (
    <Box
      gap="5px"
      alignItems="center"
      backgroundColor="bg.surface"
      rounded={6}
      borderWidth={1}
      borderColor="border.default"
      p={3}
      minW="40%"
    >
      <Flex justifyContent="space-between" alignItems="center">
        <Text fontWeight="medium">{learner ? l.learner : l.student}</Text>
        <Button
          as={RouterLink}
          to={profilePath}
          variant="secondary"
          size="2xs"
          fontWeight="semibold"
          fontSize="xs"
          backgroundColor="bg.muted"
          borderWidth={0}
        >
          {l.viewProfile}
        </Button>
      </Flex>

      <Flex mt="9px" gap={3} alignItems="center">
        <UserAvatar
          user={{
            first_name: user.first_name,
            last_name: user.last_name,
            picture_url: user.picture_url,
          }}
          size="sm"
        />
        <VStack justifyContent="start" alignItems="start" gap={0}>
          <HStack align="center" spacing={2}>
            <Text fontSize="sm" fontWeight="medium">
              {getUserFullName({
                first_name: user.first_name,
                last_name: user.last_name,
                picture_url: user.picture_url,
              })}
            </Text>
            {user.deleted_at != null ? <DeletedBadge>{l.deleted}</DeletedBadge> : null}
          </HStack>

          <HStack spacing="1">
            <CopyToClipboard value={user.email} label={l.copyEmail}>
              <Text fontSize="sm" color="bg.inverse-emphasis">
                {user.email}
              </Text>
            </CopyToClipboard>
            {user.secondaryEmail != null ? (
              <CopyToClipboardPopover value={user.secondaryEmail} />
            ) : null}
          </HStack>
        </VStack>
      </Flex>
    </Box>
  );
}

export default StudentDetails;
